// Admin-only banner shown while the DeepSeek balance is low or empty. Loads
// the current state on mount and follows `ai_balance_alert` pushes, so it
// appears for admins who log in after the balance ran out too.

import { useEffect, useState } from 'react';
import type { AiBalanceStatus } from '@marnic/shared';
import { api } from '../lib/api';
import { useSocket } from '../lib/socket';
import { useAuthStore } from '../store/auth';

const TOP_UP_URL = 'https://platform.deepseek.com/top_up';

function formatBalance(s: AiBalanceStatus): string | null {
  if (s.balance == null) return null;
  const amount = s.balance.toFixed(2);
  return s.currency === 'USD' ? `$${amount}` : `${amount} ${s.currency ?? ''}`.trim();
}

export function AiBalanceBanner(): JSX.Element | null {
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const socket = useSocket();
  const [status, setStatus] = useState<AiBalanceStatus | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get<AiBalanceStatus>('/ai/balance-status').then(setStatus).catch(() => undefined);
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || !socket) return;
    socket.on('ai_balance_alert', setStatus);
    return () => {
      socket.off('ai_balance_alert', setStatus);
    };
  }, [isAdmin, socket]);

  if (!isAdmin || !status || (status.level !== 'low' && status.level !== 'empty')) return null;

  const empty = status.level === 'empty';
  const balance = formatBalance(status);

  const recheck = () => {
    setChecking(true);
    api
      .post<AiBalanceStatus>('/ai/balance-status/refresh')
      .then(setStatus)
      .catch(() => undefined)
      .finally(() => setChecking(false));
  };

  return (
    <div
      role="alert"
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 text-sm ${
        empty ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'
      }`}
    >
      <span className="font-semibold">{empty ? 'AI analysis stopped' : 'AI balance low'}</span>
      <span className="min-w-0 flex-1">
        {status.message}
        {balance && ` Balance: ${balance}`}
        {!empty && ` (alert below ${status.threshold}).`}
        {empty && ' Failed calls are re-queued automatically once topped up.'}
      </span>
      <a
        href={TOP_UP_URL}
        target="_blank"
        rel="noreferrer"
        className="font-medium underline underline-offset-2"
      >
        Top up DeepSeek
      </a>
      <button
        type="button"
        onClick={recheck}
        disabled={checking}
        className="font-medium underline underline-offset-2 disabled:opacity-50"
      >
        {checking ? 'Checking…' : 'Re-check'}
      </button>
    </div>
  );
}
